import L from 'leaflet';
import 'leaflet-gesture-handling'; // Importa il plugin se usi Webpack/Vite

const DEBUG_MODE = false; // Imposta su false per disattivare il debug

class MapManager {
  constructor() {
    this.logDebug("📍 MapManager inizializzato");
    this.initMap();
    this.setupSearch();
  }

  logDebug(...args) {
    if (DEBUG_MODE) {
      console.log("🛠️ DEBUG:", ...args);
    }
  }

  logError(...args) {
    console.error("❌ ERRORE:", ...args);
  }

  initMap() {
    this.logDebug("🔍 Tentativo di inizializzazione della mappa...");

    const mapElement = document.querySelector(".map-container #map-canvas");
    if (!mapElement) {
      this.logError("Il div #map-canvas non è stato trovato!");
      return;
    }

    let points;
    try {
      points = JSON.parse(mapElement.dataset.points || "[]");
      this.logDebug("📌 Punti trovati:", points);
    } catch (error) {
      this.logError("Errore nel parsing dei punti:", error);
      return;
    }

    const defaultCenter = [41.8719, 12.5674];
    const defaultZoom = 10;

    this.map = L.map("map-canvas", {
      gestureHandling: true,
      center: defaultCenter,
      zoom: defaultZoom,
      minZoom: 6,
    });

    L.tileLayer(
      "https://api.mapbox.com/styles/v1/vasiker/cm71stdn4000a01r85z9l58e9/tiles/{z}/{x}/{y}?access_token=pk.eyJ1IjoidmFzaWtlciIsImEiOiJjbHYzaWF5cW4wcmY1MmxsZGMxcjRxbHZvIn0.Vu7ApQr_r8k6bRRqsHWsSA",
      {
        attribution:
          '© <a href="https://www.mapbox.com/about/maps/">Mapbox</a>',
        tileSize: 512,
        zoomOffset: -1,
      }
    ).addTo(this.map);

    this.addMarkers(points);
  }

  addMarkers(points) {
    if (this.markersGroup) {
      this.map.removeLayer(this.markersGroup);
    }

    this.markers = [];

    const validPoints = points.filter(
      (point) =>
        point.latitude &&
        point.longitude &&
        !isNaN(point.latitude) &&
        !isNaN(point.longitude)
    );

    if (validPoints.length === 0) {
      this.logDebug(
        "⚠️ Nessun punto valido trovato. La mappa potrebbe apparire vuota."
      );
      return;
    }

    const markersGroup = L.featureGroup().addTo(this.map);

    validPoints.forEach((point) => {
      try {
        const iconUrl = `/application/themes/cmc_theme_uno/dist/assets/icons/tags/tag-${point["data-tag"]}.svg`;

        const customIcon = L.icon({
          iconUrl: iconUrl,
          iconSize: [32, 32],
          iconAnchor: [16, 32],
          popupAnchor: [0, -32],
        });

        const marker = L.marker([point.latitude, point.longitude], {
          icon: customIcon,
        }).addTo(markersGroup);

        const popupContent = `
          ${point.tag ? `<p class="tag">${point.tag}</p>` : ""}
          ${point.title ? `<h3>${point.title}</h3>` : ""}
          ${
            point.name && point.tag !== "AGN Store"
            ? `<p class="name"><strong>${point.name}</strong></p>`
              : ""
          }
          ${
            point["street-address"] || point["address-locality"]
              ? `<p class="address">${point["street-address"] || ""}<br> ${
                  point["address-locality"] || ""
                }</p>`
              : ""
          }
          ${
            point.telephone
              ? point.telephone
                  .split(",")
                  .map(
                    (tel) =>
                      `<a class="phone" href="tel:${tel.trim()}">${tel.trim()}</a>`
                  )
                  .join(" ")
              : ""
          }
          ${
            point.email
              ? point.email
                  .split(",")
                  .map(
                    (mail) =>
                      `<a class="email" href="mailto:${mail.trim()}">${mail.trim()}</a>`
                  )
                  .join(" ")
              : ""
          }
          ${
            point.latitude && point.longitude
              ? `<a class="to-gmap" href="https://www.google.com/maps/search/?api=1&query=${point.latitude},${point.longitude}" target="_blank">Calcola percorso</a>`
              : ""
          }
        `;

        marker.bindPopup(popupContent);

        this.markers.push({
          id: point.id,
          marker,
          dataTag: point["data-tag"],
        });
        this.setupCardListeners(); // Attiva gli eventi sulle card dopo aver aggiunto i marker

        this.logDebug(
          `📍 Marker aggiunto: ${point.title} (${point.latitude}, ${point.longitude})`
        );
      } catch (error) {
        this.logError("Problema con l'aggiunta del marker:", error);
      }
    });

    if (validPoints.length > 0) {
      this.map.fitBounds(markersGroup.getBounds(), {
        padding: [50, 50],
        maxZoom: 10,
      });
    }
  }

  filterMarkers(selectedTag) {
    this.logDebug(`🔎 Filtro marker con tag: ${selectedTag || "tutti"}`);

    if (!this.map || !Array.isArray(this.markers)) {
      return;
    }

    const visibleMarkers = [];

    this.markers.forEach(({ marker, dataTag }) => {
      const matches =
        !selectedTag ||
        selectedTag === "generic" ||
        dataTag === selectedTag ||
        (selectedTag === "sedi" && dataTag === "rifornimenti-gpl");

      if (matches) {
        this.map.addLayer(marker);
        visibleMarkers.push(marker);
        this.logDebug(`✅ Marker visibile: ${dataTag}`);
      } else {
        this.map.removeLayer(marker);
        this.logDebug(`❌ Marker nascosto: ${dataTag}`);
      }
    });

    if (visibleMarkers.length > 0) {
      const bounds = L.featureGroup(visibleMarkers).getBounds();
      if (bounds.isValid()) {
        this.map.fitBounds(bounds, {
          padding: [50, 50],
          maxZoom: 10,
        });
      }
    }
  }

  getUserLocation() {
    if (!navigator.geolocation) {
      alert("⚠️ La geolocalizzazione non è supportata dal tuo browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;

        console.log(`📍 Posizione trovata: ${lat}, ${lon}`);

        // Rimuove il marker precedente dell'utente se esiste
        if (this.userMarker) {
          this.map.removeLayer(this.userMarker);
        }

        // Aggiunge un marker per la posizione attuale
        this.userMarker = L.marker([lat, lon], {
          icon: L.divIcon({
            className: "custom-user-marker",
            html: `<div class="pulse-marker"></div><div class="pulse-ring"></div>`,
            iconSize: [20, 20],
            iconAnchor: [10, 10],
            popupAnchor: [0, -10],
          }),
        }).addTo(this.map);

        // Trova il marker più vicino
        const closestMarker = this.findClosestMarker(lat, lon);

        if (closestMarker) {
          console.log(
            `🔍 Marker più vicino: ${closestMarker.marker.getLatLng()}`
          );

          // Apre il popup del marker più vicino
          closestMarker.marker.openPopup();

          // Centra la mappa tra la posizione dell'utente e il marker più vicino
          const bounds = L.latLngBounds(
            [lat, lon],
            closestMarker.marker.getLatLng()
          );
          this.map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
        } else {
          console.warn("⚠️ Nessun marker vicino trovato.");
          this.map.setView([lat, lon], 12); // Se nessun marker vicino, centra sulla posizione
        }
      },
      (error) => {
        console.error("❌ Errore nella geolocalizzazione:", error);
        alert(
          "⚠️ Impossibile ottenere la posizione. Assicurati che la geolocalizzazione sia attiva."
        );
      }
    );
  }

  setupSearch() {
    const searchBox = document.getElementById("search-box");
    if (!searchBox) {
      console.error("❌ ERRORE: Elemento search-box non trovato!");
      return;
    } else {
      console.log("🔍 Elemento search-box trovato!");
    }

    const geoButton = document.getElementById("geo-button");

    if (geoButton) {
      geoButton.addEventListener("click", () => {
        console.log("📍 Pulsante di geolocalizzazione cliccato!");
        this.getUserLocation(); // Assumendo che tu abbia un'istanza di MapManager
      });
    } else {
      console.error("❌ Pulsante di geolocalizzazione non trovato!");
    }

    const searchResults = document.createElement("ul");
    searchResults.classList.add("search-results");
    searchBox.parentNode.appendChild(searchResults);

    searchBox.addEventListener("input", async () => {
      const query = searchBox.value.trim(); // Rimuove spazi bianchi
      console.log(`🔍 Ricerca input: "${query}"`);

      if (query.length < 3) {
        console.warn("⚠️ Inserisci almeno 3 caratteri per cercare.");
        searchResults.innerHTML = "";
        return;
      }

      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
        query
      )}&limit=5`;
      console.log("🔗 Chiamata API:", url);

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Errore HTTP ${response.status}`);

        const data = await response.json();
        console.log("🔍 Risultati ricevuti:", data);

        searchResults.innerHTML = "";
        if (data.length === 0) {
          searchResults.innerHTML = "<li>Nessun risultato</li>";
          return;
        }

        data.forEach((place) => {
          const li = document.createElement("li");
          li.textContent = place.display_name;
          li.addEventListener("click", () => {
            console.log(
              `✅ Selezionato: ${place.display_name} (${place.lat}, ${place.lon})`
            );
            searchBox.value = place.display_name;
            searchResults.innerHTML = "";
            this.searchLocation(parseFloat(place.lat), parseFloat(place.lon));
          });
          searchResults.appendChild(li);
        });
      } catch (error) {
        console.error("❌ ERRORE nella richiesta API:", error);
      }
    });

    document.addEventListener("click", (e) => {
      if (!searchBox.contains(e.target) && !searchResults.contains(e.target)) {
        searchResults.innerHTML = "";
      }
    });
  }

  findClosestMarker(lat, lon) {
    if (!this.markers || this.markers.length === 0) {
      console.warn("⚠️ Nessun marker disponibile per la ricerca.");
      return null;
    }

    const userPosition = L.latLng(lat, lon);

    // Trova il marker più vicino
    return (
      this.markers
        .map((m) => ({
          ...m,
          distance: userPosition.distanceTo(m.marker.getLatLng()),
        }))
        .sort((a, b) => a.distance - b.distance)[0] || null
    );
  }

  async searchLocation(lat, lon, query = "") {
    if (!lat || !lon) {
      if (query.length < 3) return;

      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
        query
      )}&limit=1`;
      console.log("🔗 Chiamata API per ottenere coordinate:", url);

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Errore HTTP ${response.status}`);

        const data = await response.json();
        if (data.length === 0) {
          alert("⚠️ Indirizzo non trovato. Riprova con un altro termine.");
          return;
        }

        lat = parseFloat(data[0].lat);
        lon = parseFloat(data[0].lon);
        console.log(`📍 Coordinate trovate: ${lat}, ${lon}`);
      } catch (error) {
        console.error("❌ ERRORE nella ricerca:", error);
        return;
      }
    }

    console.log(`🔍 Sposto la mappa a: ${lat}, ${lon}`);

    // Rimuove il marker precedente della ricerca se esiste
    if (this.searchMarker) {
      this.map.removeLayer(this.searchMarker);
    }

    // Aggiunge un nuovo marker sulla posizione cercata
    this.searchMarker = L.marker([lat, lon], {
      icon: L.icon({
        iconUrl: "https://maps.google.com/mapfiles/ms/icons/red-dot.png", // Icona Google Maps
        shadowUrl:
          "https://maps.google.com/mapfiles/ms/micons/msmarker.shadow.png", // Ombra come in Google Maps
        iconSize: [32, 32],
        iconAnchor: [16, 32], // Punta in basso come Google Maps
        popupAnchor: [0, -32],
      }),
    }).addTo(this.map);

    // Aggiunge un popup al marker della ricerca
    this.searchMarker
      .bindPopup(`📍 Punto cercato: ${query || "Posizione trovata"}`)
      .openPopup();

    // Trova il marker più vicino
    const closestMarker = this.findClosestMarker(lat, lon);

    if (closestMarker) {
      console.log(`🔍 Marker più vicino: ${closestMarker.marker.getLatLng()}`);

      // Apre il popup del marker più vicino
      closestMarker.marker.openPopup();

      // Centra la mappa tra la posizione cercata e il marker più vicino
      const bounds = L.latLngBounds(
        [lat, lon],
        closestMarker.marker.getLatLng()
      );
      this.map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
    } else {
      console.warn("⚠️ Nessun marker vicino trovato.");
      this.map.setView([lat, lon], 12); // Se nessun marker vicino, centra solo sulla posizione
    }
  }
  setupCardListeners() {
    const cards = document.querySelectorAll(".tile.store");
    const mapContainer = document.querySelector(".map-container");

    cards.forEach((card) => {
      card.addEventListener("click", () => {
        const selectedMarkerId = card.getAttribute("data-id");
        const selectedMarker = this.markers.find(
          (m) => m.id === selectedMarkerId
        );

        if (!selectedMarker) {
          console.warn(`⚠️ Nessun marker trovato per ID: ${selectedMarkerId}`);
          return;
        }

        // Chiude tutti i popup e mostra solo quello selezionato
        this.markers.forEach(({ marker }) => marker.closePopup());

        // Apre il popup e centra la mappa sul marker selezionato
        selectedMarker.marker.openPopup();
        this.map.setView(selectedMarker.marker.getLatLng(), 14);

        if (mapContainer) {
          mapContainer.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    });
  }
}

export default MapManager;
